import React from 'react';
import PropTypes from 'prop-types';
import {
  Page,
  View,
  Text,
} from '@react-pdf/renderer';

import TablePageHeader from '../../components/TablePageHeader';
import BottomPagination from '../../components/BottomPagination';
import DraftOverlay from '../../components/DraftOverlay';

import { commaEvery3rdChar, formalizeClassName } from '../../../../../../utils';

const ROWS_PER_PAGE = 16;
const MIN_CONTINUATION_ROWS = 4;
const MIN_ROWS_TO_START_FUND = 5;
// Own block: dark header + column headers + one total row (same units as a fund table).
const FIRM_TOTAL_COST = 3;

function holdingFundNames(tableData) {
  return Object.keys(tableData?.holdingsObject || {}).filter((fundName) => fundName !== 'Project Totals');
}

// A table titled with the client name (or whose shares exactly equal every other
// vehicle combined) is a firm rollup, not a distinct fund.
function rollupFundName(tableData, clientName) {
  const fundsList = holdingFundNames(tableData);
  if (fundsList.length <= 1) return null;
  const matches = fundsList.filter((fundName) => {
    const others = fundsList.filter((name) => name !== fundName);
    const otherShares = others.reduce(
      (sum, name) => sum + Number(tableData.holdingsObject[name]?.['Number of Shares'] || 0),
      0,
    );
    const fundShares = Number(tableData.holdingsObject[fundName]?.['Number of Shares'] || 0);
    return fundShares && Math.abs(fundShares - otherShares) < 1;
  });
  if (clientName && matches.includes(clientName)) return clientName;
  return matches.length === 1 ? matches[0] : null;
}

function vehicleFundNames(tableData, clientName) {
  const rollup = rollupFundName(tableData, clientName);
  return holdingFundNames(tableData).filter((name) => name !== rollup);
}

function vehicleTotals(tableData, clientName) {
  return vehicleFundNames(tableData, clientName).reduce(
    (acc, name) => ({
      shares: acc.shares + Number(tableData.holdingsObject[name]?.['Number of Shares'] || 0),
      value: acc.value + Number(tableData.holdingsObject[name]?.['Aggregate Value'] || 0),
    }),
    { shares: 0, value: 0 },
  );
}

// Shared pagination builder so the page-count estimate (used for TOC/footer numbering)
// and the actual render loop can never drift apart.
function buildHoldingsPages(reportData) {
  const tableData = reportData?.FundHoldingsSummary;
  if (!tableData?.holdingsObject) return [];
  const clientName = reportData?.companyInfo?.investorFirmName;
  const fundsList = vehicleFundNames(tableData, clientName);

  const pages = [];
  let currentPage = { funds: [], showFirmTotal: false };
  let usedRows = 0;

  const pushPage = () => {
    if (currentPage.funds.length || currentPage.showFirmTotal) pages.push(currentPage);
    currentPage = { funds: [], showFirmTotal: false };
    usedRows = 0;
  };

  const fundCost = (rowCount, includesFundTotal) => (
    2 + rowCount + (includesFundTotal ? 1 : 0)
  );

  fundsList.forEach((fundName) => {
    const ownershipClasses = tableData.holdingsObject[fundName].Holdings['Ownership Class'];
    let rowStart = 0;

    while (rowStart < ownershipClasses.length) {
      const remaining = ownershipClasses.length - rowStart;
      const headerCost = 2;
      // Fit as many rows as possible on the current page; avoid over-reserving a blank row.
      let available = Math.max(ROWS_PER_PAGE - usedRows - headerCost, 1);

      // If only a tiny continuation would remain after filling this page, pull those
      // leftover rows onto this page when they fit (prevents orphan 1-3 row pages).
      if (remaining > available && remaining - available < MIN_CONTINUATION_ROWS) {
        const needed = remaining;
        if (usedRows + fundCost(needed, true) <= ROWS_PER_PAGE) {
          available = needed;
        }
      }

      // If this page already has content and there isn't room for a meaningful fund block,
      // start a fresh page instead of leaving a sparse leftover.
      const provisionalEnd = Math.min(rowStart + available, ownershipClasses.length);
      const provisionalRows = provisionalEnd - rowStart;
      const includesFundTotal = provisionalEnd >= ownershipClasses.length;
      const blockCost = fundCost(provisionalRows, includesFundTotal);

      const shouldStartNewPageForSpace = usedRows > 0 && usedRows + blockCost > ROWS_PER_PAGE;
      const shouldStartNewPageForFund = (
        usedRows > 0 &&
        rowStart === 0 &&
        ROWS_PER_PAGE - usedRows < MIN_ROWS_TO_START_FUND &&
        remaining + headerCost > ROWS_PER_PAGE - usedRows
      );

      if (shouldStartNewPageForSpace || shouldStartNewPageForFund) {
        pushPage();
      } else {
        const rowEnd = Math.min(rowStart + Math.max(ROWS_PER_PAGE - usedRows - headerCost, 1), ownershipClasses.length);
        const isContinuation = rowStart > 0;
        const finalIncludesFundTotal = rowEnd >= ownershipClasses.length;

        currentPage.funds.push({
          fundName,
          rowStart,
          rowEnd,
          isContinuation,
          includesFundTotal: finalIncludesFundTotal,
        });
        usedRows += fundCost(rowEnd - rowStart, finalIncludesFundTotal);
        rowStart = rowEnd;

        if (usedRows >= ROWS_PER_PAGE && rowStart < ownershipClasses.length) {
          pushPage();
        }
      }
    }
  });
  pushPage();

  // Post-pass: merge a trailing orphan continuation (few rows alone on last page)
  // back onto the previous page when capacity allows.
  if (pages.length >= 2) {
    const last = pages[pages.length - 1];
    const prev = pages[pages.length - 2];
    if (last.funds.length === 1) {
      const orphan = last.funds[0];
      const orphanRows = orphan.rowEnd - orphan.rowStart;
      if (orphan.isContinuation && orphanRows < MIN_CONTINUATION_ROWS) {
        const prevUsed = prev.funds.reduce(
          (sum, f) => sum + fundCost(f.rowEnd - f.rowStart, f.includesFundTotal),
          0,
        );
        const prevFund = [...prev.funds].reverse().find((f) => f.fundName === orphan.fundName);
        if (prevFund && prevUsed - 2 + orphanRows + (orphan.includesFundTotal ? 1 : 0) <= ROWS_PER_PAGE) {
          prevFund.rowEnd = orphan.rowEnd;
          prevFund.includesFundTotal = orphan.includesFundTotal;
          pages.pop();
        }
      }
    }
  }

  // Firm-level total is its own block, never a row inside the last vehicle table.
  if (fundsList.length && pages.length) {
    const last = pages[pages.length - 1];
    const lastUsed = last.funds.reduce(
      (sum, f) => sum + fundCost(f.rowEnd - f.rowStart, f.includesFundTotal),
      0,
    );
    if (lastUsed + FIRM_TOTAL_COST > ROWS_PER_PAGE) {
      pages.push({ funds: [], showFirmTotal: true });
    } else {
      last.showFirmTotal = true;
    }
  }

  return pages;
}

export function getHoldingsPageCount(reportData) {
  const pages = buildHoldingsPages(reportData);
  return Math.max(pages.length, 1);
}

export default function Holdings({ reportData, exhibitNumber, startPageNumber = 4 }) {
  const tableData = reportData.FundHoldingsSummary;

  const tableHeaders = [
    'Ownership class',
    'Original issue price\nor strike price',
    'Fair value per share',
    'Number of shares',
    'Aggregate value',
  ];

  function formatFixed(val, decimals) {
    if (val == null || val === '') return null;
    const num = Number(val);
    if (Number.isNaN(num)) return null;
    return num.toFixed(decimals);
  }

  function returnCellData(cellData, dataName, rowIndex) {
    if (dataName === 'Original issue price\nor strike price') {
      const className = cellData['Ownership Class']?.[rowIndex] || '';
      const raw = cellData['Original Issue Price'][rowIndex];
      if (/^common$/i.test(String(className).trim()) && (!raw || Number(raw) === 0)) return 'N/A';
      const formatted = formatFixed(raw, 4);
      return formatted != null ? `$${formatted}` : null;
    }
    if (dataName === 'Fair value per share') {
      const formatted = formatFixed(cellData['Fair Value Per Share'][rowIndex], 4);
      return formatted != null ? `$${formatted}` : null;
    }
    if (dataName === 'Number of shares') {
      const shares = cellData['Number of Shares'][rowIndex];
      return shares != null ? commaEvery3rdChar(shares) : null;
    }
    if (dataName === 'Aggregate value') {
      const formatted = formatFixed(cellData['Aggregate Value'][rowIndex], 0);
      return formatted != null ? `$${commaEvery3rdChar(formatted)}` : null;
    }
    if (dataName === 'Ownership class') {
      return formalizeClassName(cellData['Ownership Class']?.[rowIndex]);
    }
    const formattedDataName = dataName.split(' ').map((word) => word !== 'of' ? word[0].toUpperCase() + word.substr(1) : word).join(' ');
    const value = cellData[formattedDataName][rowIndex];
    return value != null ? value.toString() : null;
  }

  const footnote = reportData.footnoteData.fundHoldingSummary;

  const pages = buildHoldingsPages(reportData);

  const renderFundBlock = (fundBlock, fundBlockIndex, pageIndex) => {
    const {
      fundName,
      rowStart,
      rowEnd,
      isContinuation,
      includesFundTotal,
    } = fundBlock;
    const holdings = tableData.holdingsObject[fundName].Holdings;
    const titleSuffix = isContinuation ? ' (continued)' : '';

    return (
      <View
        key={`${fundName}-${rowStart}-${pageIndex}`}
        style={{
          marginTop: fundBlockIndex === 0 ? '84px' : '16px',
          marginLeft: '80px',
          display: 'flex',
          flexDirection: 'column',
          width: '1064px',
          border: '1px',
          borderColor: '#AFB1B3',
        }}
      >
        <View
          style={{
            height: '38px',
            backgroundColor: '#0C2617',
            borderBottom: '1px',
            borderColor: '#CCCCCC',
          }}
        >
          <Text
            style={{
              fontFamily: 'RobotoMed',
              fontSize: '14px',
              color: '#E1E2EC',
              marginTop: 'auto',
              paddingBottom: '8px',
              paddingLeft: '16px',
              letterSpacing: '0.08px',
            }}
          >
            {fundName}
            {titleSuffix}
            {' '}
            <Text style={{ color: '#8AB9A4' }}>(USD$)</Text>
          </Text>
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            height: '42px',
            backgroundColor: '#307157',
            borderBottom: '1px',
            borderColor: '#CCCCCC',
          }}
        >
          {tableHeaders.map((dataName, index) => (
            <Text
              key={dataName}
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '12px',
                color: '#E1E2EC',
                width: '200px',
                marginTop: 'auto',
                paddingBottom: '4px',
                paddingLeft: '16px',
                letterSpacing: '0.5px',
                textAlign: index !== 0 && 'right',
              }}
            >
              {dataName}
            </Text>
          ))}
        </View>
        {holdings['Ownership Class'].slice(rowStart, rowEnd).map((cellData, localIndex) => {
          const rowIndex = rowStart + localIndex;
          return (
            <View
              key={`${cellData}-${rowIndex}`}
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                height: '24px',
                backgroundColor: `${rowIndex % 2 ? '#F0F0F3' : '#FAFAFD'}`,
              }}
            >
              {tableHeaders.map((dataName, cellIndex) => (
                <Text
                  key={dataName}
                  style={{
                    fontFamily: 'Roboto',
                    fontSize: '12px',
                    color: '#49454F',
                    width: '200px',
                    paddingLeft: '16px',
                    letterSpacing: '0.4px',
                    textAlign: cellIndex !== 0 && 'right',
                  }}
                >
                  {returnCellData(holdings, dataName, rowIndex) || 'N/A'}
                </Text>
              ))}
            </View>
          );
        })}
        {includesFundTotal && (
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              height: '24px',
              backgroundColor: '#9ECEBB',
            }}
          >
            <Text
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '12px',
                color: '#49454F',
                width: '600px',
                paddingLeft: '16px',
                letterSpacing: '0.5px',
              }}
            >
              Fair value of
              {' '}
              {fundName}
            </Text>
            <Text
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '12px',
                color: '#49454F',
                width: '200px',
                paddingLeft: '16px',
                letterSpacing: '0.4px',
                textAlign: 'right',
              }}
            >
              {commaEvery3rdChar(tableData.holdingsObject[fundName]['Number of Shares'].toString()) || 'N/A'}
            </Text>
            <Text
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '12px',
                color: '#49454F',
                width: '200px',
                paddingLeft: '16px',
                letterSpacing: '0.4px',
                textAlign: 'right',
              }}
            >
              {(() => {
                const formatted = formatFixed(tableData.holdingsObject[fundName]['Aggregate Value'], 0);
                return formatted != null ? `$${commaEvery3rdChar(formatted)}` : 'N/A';
              })()}
            </Text>
          </View>
        )}
      </View>
    );
  };

  const renderFirmTotalBlock = (isFirstBlockOnPage) => {
    const clientName = reportData.companyInfo?.investorFirmName || 'client';
    const totals = vehicleTotals(tableData, clientName);
    const formattedValue = formatFixed(totals.value, 0);
    return (
      <View
        style={{
          marginTop: isFirstBlockOnPage ? '84px' : '16px',
          marginLeft: '80px',
          display: 'flex',
          flexDirection: 'column',
          width: '1064px',
          border: '1px',
          borderColor: '#AFB1B3',
        }}
      >
        <View
          style={{
            height: '38px',
            backgroundColor: '#0C2617',
            borderBottom: '1px',
            borderColor: '#CCCCCC',
          }}
        >
          <Text
            style={{
              fontFamily: 'RobotoMed',
              fontSize: '14px',
              color: '#E1E2EC',
              marginTop: 'auto',
              paddingBottom: '8px',
              paddingLeft: '16px',
              letterSpacing: '0.08px',
            }}
          >
            {`Total — all ${clientName} vehicles`}
            {' '}
            <Text style={{ color: '#8AB9A4' }}>(USD$)</Text>
          </Text>
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            height: '42px',
            backgroundColor: '#307157',
            borderBottom: '1px',
            borderColor: '#CCCCCC',
          }}
        >
          {tableHeaders.map((dataName, index) => (
            <Text
              key={dataName}
              style={{
                fontFamily: 'RobotoMed',
                fontSize: '12px',
                color: '#E1E2EC',
                width: '200px',
                marginTop: 'auto',
                paddingBottom: '4px',
                paddingLeft: '16px',
                letterSpacing: '0.5px',
                textAlign: index !== 0 && 'right',
              }}
            >
              {dataName}
            </Text>
          ))}
        </View>
        <View
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            height: '24px',
            backgroundColor: '#9ECEBB',
          }}
        >
          <Text
            style={{
              fontFamily: 'RobotoMed',
              fontSize: '12px',
              color: '#49454F',
              width: '600px',
              paddingLeft: '16px',
              letterSpacing: '0.5px',
            }}
          >
            Fair value of all holdings
          </Text>
          <Text
            style={{
              fontFamily: 'RobotoMed',
              fontSize: '12px',
              color: '#49454F',
              width: '200px',
              paddingLeft: '16px',
              letterSpacing: '0.4px',
              textAlign: 'right',
            }}
          >
            {commaEvery3rdChar(totals.shares.toString()) || 'N/A'}
          </Text>
          <Text
            style={{
              fontFamily: 'RobotoMed',
              fontSize: '12px',
              color: '#49454F',
              width: '200px',
              paddingLeft: '16px',
              letterSpacing: '0.4px',
              textAlign: 'right',
            }}
          >
            {formattedValue != null ? `$${commaEvery3rdChar(formattedValue)}` : 'N/A'}
          </Text>
        </View>
      </View>
    );
  };

  return (
    <>
      {pages.map((page, pageIndex) => {
        const pageKey = page.funds.map((f) => `${f.fundName}-${f.rowStart}-${f.rowEnd}`).join('|')
          || `holdings-total-${startPageNumber + pageIndex}`;
        return (
          <Page
            key={pageKey}
            size="TABLOID"
            orientation="landscape"
            style={{ position: 'relative', backgroundColor: '#EAEAEB' }}
          >
            <TablePageHeader
              companyName={reportData.companyInfo.companyName}
              pageName={`Fund Holdings Summary${pageIndex > 0 ? ' (continued)' : ''}   |   Exhibit ${exhibitNumber}`}
            />
            {page.funds.map((fundBlock, fundBlockIndex) => renderFundBlock(fundBlock, fundBlockIndex, pageIndex))}
            {page.showFirmTotal && renderFirmTotalBlock(page.funds.length === 0)}
            {pageIndex === pages.length - 1 && !!footnote && (
              <Text
                style={{
                  fontFamily: 'Roboto',
                  fontSize: '8px',
                  color: '#49454F',
                  letterSpacing: '0.4px',
                  marginTop: '16px',
                  marginLeft: '90px',
                  width: '450px',
                  lineHeight: '0.55mm',
                }}
              >
                Footnote:&nbsp;
                {footnote}
              </Text>
            )}
            <BottomPagination
              pageNumber={startPageNumber + pageIndex}
              reportData={reportData}
            />
            {reportData.isDraft && <DraftOverlay />}
          </Page>
        );
      })}
    </>
  );
}

Holdings.propTypes = {
  reportData: PropTypes.object.isRequired,
  exhibitNumber: PropTypes.number.isRequired,
  startPageNumber: PropTypes.number,
};
